import { Button, cn, toast } from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { File, Loader2, Paperclip, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  confirmUnitDocument,
  deleteUnitDocument,
  fetchUnitDocumentSignedUrl,
  presignUnitDocument,
} from '../../../../api/units.js';
import { ApiError } from '../../../../lib/api-client.js';

import type { LeadAttachedDocument } from '../../../../api/lead.js';
import type { UnitDocumentKind } from '@sitewise/shared';


interface Props {
  unitId: string;
  /** The currently attached document, if any. The parent owns the
   *  document_id field on `unit_lead_status`; this widget calls the
   *  presign/confirm flow and then asks the parent to update the
   *  reference via `onAttached(documentId)`. */
  document: LeadAttachedDocument | null;
  kind: UnitDocumentKind;
  label: string;
  helpText?: string;
  /** Fired after a successful upload — pass the new document id back
   *  to the parent so it can `PUT /units/:id/lead` with the new
   *  `*_document_id` field. */
  onAttached: (documentId: string) => Promise<void> | void;
  /** Fired when the user removes the attachment. The parent should
   *  send a `PUT` with `null` for the document id; it can also
   *  optionally soft-delete the underlying document row. */
  onDetached: () => Promise<void> | void;
  disabled?: boolean;
}

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB — generous for PDFs and photos
const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp,image/heic';

/**
 * Reusable upload/view/remove slot for a single document attached to
 * a unit_lead_status field. Used by both the exemption-letter and
 * the XRF-report fields on the unit Lead tab. Bytes go directly to
 * Spaces via the presigned PUT URL the API hands back; we only
 * persist metadata + the foreign key on the lead row.
 */
export function DocumentSlot({
  unitId,
  document,
  kind,
  label,
  helpText,
  onAttached,
  onDetached,
  disabled,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_BYTES) {
        throw new Error('File is larger than 25 MB');
      }
      const presign = await presignUnitDocument(unitId, {
        kind,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        caption: null,
      });
      const upload = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: presign.headers,
        body: file,
      });
      if (!upload.ok) {
        throw new Error(`Upload failed: ${upload.status}`);
      }
      const confirmed = await confirmUnitDocument(unitId, presign.document.id);
      return confirmed.document;
    },
    onMutate: () => setUploading(true),
    onSettled: () => setUploading(false),
    onSuccess: async (doc) => {
      await onAttached(doc.id);
      toast.success(`${label} attached.`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const detachMutation = useMutation({
    mutationFn: async () => {
      if (document) {
        // Soft-delete the underlying document so the Documents tab
        // doesn't keep showing it as an orphan. Reference clearing
        // on the lead row is the parent's job.
        await deleteUnitDocument(unitId, document.id).catch(() => undefined);
      }
      await onDetached();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  async function handleView() {
    if (!document) return;
    try {
      const signed = await fetchUnitDocumentSignedUrl(unitId, document.id);
      window.open(signed.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not open document');
    }
  }

  function handleFile(file: File | null) {
    if (!file) return;
    uploadMutation.mutate(file);
  }

  const busy = uploading || uploadMutation.isPending || detachMutation.isPending;

  return (
    <div className="rounded-md border border-dashed border-border bg-surface-muted/40 p-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-medium">
            <Paperclip className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
            {label}
          </div>
          {document ? (
            <button
              type="button"
              onClick={handleView}
              className={cn(
                'mt-1 flex max-w-full items-center gap-1.5 truncate text-xs text-primary',
                'hover:underline focus-visible:outline-none focus-visible:underline',
              )}
              title={document.originalFilename}
            >
              <File className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">{document.originalFilename}</span>
              <span className="shrink-0 text-fg-muted">({formatBytes(document.bytes)})</span>
            </button>
          ) : (
            <p className="mt-1 text-xs text-fg-muted">
              {helpText ?? 'No file attached.'}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              handleFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button
            size="sm"
            variant={document ? 'outline' : 'default'}
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {document ? 'Replace' : 'Upload'}
          </Button>
          {document ? (
            <Button
              size="icon"
              variant="ghost"
              aria-label="Remove attachment"
              disabled={disabled || busy}
              onClick={() => detachMutation.mutate()}
            >
              <Trash2 className="h-4 w-4 text-fg-muted" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
